import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '../i18n';
import { claimChunkReload, isChunkLoadError, sessionStorageOrNull } from '../utils/chunkReload';

interface Props {
  children: ReactNode;
  /** When this key changes, the error state is reset (used with the current route path). */
  resetKey?: string | number;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render errors below it and shows a calm fallback with a manual
 * "רענון הדף" button (T-14, F-027).
 *
 * It reloads automatically ONLY for a failed lazy-chunk load (a tab still on an
 * old build after a deploy), and at most `MAX_CHUNK_RELOADS` times per window,
 * counted in sessionStorage so the count survives the reload. Any other error
 * shows the fallback and never reloads: reloading would hit the same error
 * again and loop.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
    if (isChunkLoadError(error) && claimChunkReload(sessionStorageOrNull(), Date.now())) {
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps: Props) {
    // A route change clears a previously shown error instead of keeping it stuck.
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const t = (key: string) => i18n.t(key);

      return (
        <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4" role="alert">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-danger-100 flex items-center justify-center mb-6">
              <AlertTriangle className="text-danger-600" size={40} aria-hidden="true" />
            </div>
            <h1 className="text-xl font-semibold text-surface-900 mb-3">
              {t('errorBoundary.title')}
            </h1>
            <p className="text-surface-600 mb-8">
              {t('errorBoundary.message')}
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="btn btn-primary"
            >
              <RefreshCw size={18} aria-hidden="true" />
              {t('errorBoundary.retry')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
